"use client";

import clsx from "clsx";
import React, { useState } from "react";
import Image from "next/image";

// Flags & Icons
import usaFlag from "@/app/assets/images/flags/usa.png";
import ukFlag from "@/app/assets/images/flags/uk.png";
import uaeFlag from "@/app/assets/images/flags/uae.png";
import indiaFlag from "@/app/assets/images/flags/india.png";
import teamsIcon from "@/app/assets/images/teams.png";
import gmailIcon from "@/app/assets/images/gmail.png";

// Side Illustrations
import salesIllustration from "@/app/assets/images/sales.png";
import hrIllustration from "@/app/assets/images/connect.png";

export default function GlobalContactButton({
  className,
  dark = true,
}: {
  className?: string;
  dark?: boolean;
}) {
  const [showMainDropdown, setShowMainDropdown] = useState(false);

  const flags = [
    { id: "us", name: "USA", src: usaFlag },
    { id: "uk", name: "UK", src: ukFlag },
    { id: "uae", name: "UAE", src: uaeFlag },
    { id: "in", name: "India", src: indiaFlag },
  ];

  return (
    <div
      className={clsx("relative flex items-center", className)}
      onMouseEnter={() => setShowMainDropdown(true)}
      onMouseLeave={() => setShowMainDropdown(false)}
    >
      {/* Main Button Pill */}
      <div className="relative inline-flex items-center w-[54px] h-[54px] z-20 cursor-pointer">
        <div
          className={clsx(
            "absolute inset-0 rounded-full border transition-all duration-300",
            dark ? "bg-white/5 border-white/10" : "bg-black/5 border-black/10",
            showMainDropdown && (dark ? "bg-white/10" : "bg-black/10"),
          )}
          style={{
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
        />

        <div className="relative z-30 flex items-center justify-center w-full h-full px-1.5 gap-2">
          <div
            className="relative flex items-center justify-center w-[42px] h-[42px] rounded-full"
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(6.3px)",
              WebkitBackdropFilter: "blur(6.3px)",
              boxShadow: `0px 3.66px 3.06px 0px #FFFFFF21 inset, 0px -3.66px 1.83px 0px #0000001A inset, 0px 1px 4px 0px #FFFFFF inset, 0px 4px 4px 0px #00000040`,
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={dark ? "white" : "#1A1A1A"}
            >
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </svg>
          </div>
        </div>
      </div>

      {/* --- MEGA MENU --- */}
      {showMainDropdown && (
        <div className="absolute top-[48px] left-0 z-[1000] pt-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-b-white ml-6" />

          <div className="bg-white rounded-[15px] shadow-2xl w-[340px] overflow-hidden text-[#1A1A1A] text-left">
            <div className="px-5 py-2 border-b border-[#ECF0F6]">
              <h3 className="font-bold text-[18px]">Connect With Us</h3>
            </div>

            {/* SALES SECTION */}
            <div className="p-5 flex gap-4">
              <div className="w-12 h-12 flex-shrink-0 mt-1">
                <Image
                  src={salesIllustration}
                  alt="Sales"
                  className="object-contain"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 space-y-3">
                <p className="text-[#5C5C5E] text-[12px] font-bold uppercase tracking-wider">
                  For Sales Inquiry
                </p>
                <div className="space-y-2.5">
                  {/* FUNCTIONAL LINKS */}
                  <ContactItem
                    icon={gmailIcon}
                    text="sales@konstantinfo.com"
                    href="mailto:sales@konstantinfo.com"
                    isEmail
                  />
                  <ContactItem
                    icon={teamsIcon}
                    text="Connect On MS Teams"
                    href="https://teams.microsoft.com/l/chat/0/0?users=mail@konstantinfosolutions.com"
                  />
                  <ContactItem
                    icon={usaFlag}
                    text="+1-315-505-4567 (Sales)"
                    href={"tel:+1-315-505-4567"}
                  />
                  <ContactItem
                    icon={indiaFlag}
                    text="+91-958-749-7499 (Sales)"
                    href={"tel:+91-958-749-7499"}
                  />
                  <ContactItem
                    icon={usaFlag}
                    text="+1-310-933-5465 (Office)"
                    href={"tel:+1-310-933-5465"}
                  />
                  <ContactItem
                    icon={ukFlag}
                    text="+44-203-239-7253"
                    href={"tel:+44-203-239-7253"}
                  />
                </div>
              </div>
            </div>

            {/* HR SECTION */}
            <div className="px-5 py-5 bg-white border-t border-[#ECF0F6] flex gap-4">
              <div className="w-12 h-12 flex-shrink-0 mt-1">
                <Image
                  src={hrIllustration}
                  alt="HR"
                  className="object-contain"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 space-y-2">
                <p className="text-[#5C5C5E] text-[12px] font-bold uppercase tracking-wider">
                  For HR Inquiry
                </p>
                <div className="flex">
                  <ContactItem
                    icon={indiaFlag}
                    text="+91-141-2291398"
                    href={"tel:+91-141-2291398"}
                  />
                  <span>,</span>
                  <ContactItem
                    icon={""}
                    text="4028078"
                    href={"tel:+91-141-4028078"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactItem({
  icon,
  text,
  isEmail = false,
  href,
}: {
  icon: any;
  text: string;
  isEmail?: boolean;
  href?: string;
}) {
  const content = (
    <>
      {icon ? (
        <div className="relative w-5 h-5 flex-shrink-0 overflow-hidden rounded-sm">
          {" "}
          <Image
            src={icon}
            alt=""
            className="object-cover w-full h-full"
            loading="lazy"
            sizes="(max-width: 1024px) 100vw, 50vw"
          />{" "}
        </div>
      ) : null}

      <span
        className={clsx(
          "text-[14px] font-bold transition-colors",
          isEmail
            ? "text-[#1A1A1A] group-hover:text-[#0066FF]"
            : "text-[#1A1A1A] group-hover:text-black",
        )}
      >
        {text}
      </span>
    </>
  );

  const wrapperClass =
    "flex items-center gap-3 group cursor-pointer no-underline";

  if (href) {
    return (
      <a
        href={href}
        className={wrapperClass}
        target={isEmail ? "_self" : "_blank"}
        rel="noreferrer"
      >
        {content}
      </a>
    );
  }

  return <div className={wrapperClass}>{content}</div>;
}
